import React, { memo, useState, ChangeEvent, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Button } from 'transfix-ui/components/Button'
import { FormModal } from 'transfix-ui/components/Modals'
import uuid from 'uuid'
import { IFile, IBulkUploadFormState, IValidation } from 'apps/rms/types/shipperRates'
import { bulkUpdateShipperRate, bulkCreateShipperRate } from 'apps/rms/utils/actions/shipperRates'
import { validateBulkUploadForm } from 'apps/rms/utils/validators'
import { TextInput } from 'components/atoms'
import { AttachmentIcon } from 'transfix-icons'
import { filePromises } from 'utils/fileUploadHelper'

const CREATE_TEMPLATE_URL =
  'https://docs.google.com/spreadsheets/d/19fOHZOWIJn7xCoM9dnS0rzoR8U3-g5317M-Ic7Cs-vM'
const UPDATE_TEMPLATE_URL =
  'https://docs.google.com/spreadsheets/d/17UljSKSsFbXP01hKxCB-eEEFs6-ewVb4ITxwUrsugsc'

interface IShipperRateModalProps {
  showModal: boolean
  errors: string
  isUpdate: boolean
  closeModalHandler: () => void
}

const initialValidation = {
  pristine: true,
  errors: {},
} as IValidation

export const BulkRatesModal = memo<IShipperRateModalProps>(
  ({ closeModalHandler, errors, isUpdate, showModal }) => {
    const hiddenFileInput = useRef<HTMLInputElement>(null)
    const dispatch = useDispatch()

    const [formState, setFormState] = useState<IBulkUploadFormState>({})
    const [validation, setValidation] = useState<IValidation>(initialValidation)
    const { errors: validationErrors, pristine } = validation

    const { file = {} } = formState
    const { fileFileName } = file

    const validate = (form: RecursivePartial<IBulkUploadFormState>) => {
      try {
        validateBulkUploadForm(form)
      } catch (errs) {
        setValidation({ pristine: false, errors: errs })
        return false
      }
      setValidation({ pristine: false, errors: {} })
      return true
    }

    const updateForm = (change: Partial<IBulkUploadFormState>) => {
      setFormState((current: IBulkUploadFormState) => {
        const nextState = { ...current, ...change }
        if (!pristine) {
          validate(nextState)
        }
        return nextState
      })
    }

    const fileUploadHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const {
        target: { files },
      } = e
      if (files) {
        const fileList = Array.from(files)
        Promise.all(filePromises(fileList)).then(packageFiles => {
          const pkgFiles = packageFiles.map(pkg => {
            const pkgFile = pkg as IFile
            pkgFile.uuid = uuid()
            return pkgFile
          })
          updateForm({ file: pkgFiles[0] })
        })
      }
    }

    const emailHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      updateForm({ email: value })
    }

    const openFileUploadDialog = () => {
      if (hiddenFileInput.current) {
        hiddenFileInput.current.click()
      }
    }

    const submitForm = () => {
      if (validate(formState)) {
        if (isUpdate) {
          dispatch(bulkUpdateShipperRate(formState))
        } else {
          dispatch(bulkCreateShipperRate(formState))
        }
        closeModalHandler()
      }
    }

    const { email: emailError, file: fileError } = validationErrors || {}

    return (
      <FormModal
        title={`${isUpdate ? 'Update' : 'Add'} Multiple Rates`}
        size={6}
        open={showModal}
        onClose={() => {
          setFormState({ file: undefined })
          closeModalHandler()
        }}
        dataTest='modal-bulk-shipper-rate'
        error={errors}
        actions={[
          {
            label: 'Cancel',
            type: 'dismiss',
            dataTest: 'cancel-shipper-rate-button',
          },
          {
            label: 'Upload',
            type: 'confirm',
            action: submitForm,
            dataTest: 'bulk-save-button',
          },
        ]}>
        <div className='row'>
          <div className='col-xs-12 col-md-12 col-lg-6 mt4 pt1'>
            <Button
              startIcon={<AttachmentIcon />}
              onClick={openFileUploadDialog}
              data-test='upload-files'
              size='large'
              color='tertiary'>
              Attach Template
            </Button>
            <input
              style={{ display: 'none' }}
              type='file'
              id='uploads'
              name='uploads'
              accept='.csv'
              data-test='upload-input'
              onChange={fileUploadHandler}
              ref={hiddenFileInput}
            />
            <div>
              <a
                href={isUpdate ? UPDATE_TEMPLATE_URL : CREATE_TEMPLATE_URL}
                target='_blank'
                rel='noopener noreferrer'>
                Download Template
              </a>
            </div>
            {fileError && (
              <span data-test='file-error' className='validation-message text-error'>
                {fileError}
              </span>
            )}
            <p>{fileFileName}</p>
          </div>
          <div className='col-xs-12 col-md-12 col-lg-6'>
            <TextInput
              label='To Email'
              placeholder='Enter email to send results to'
              onChange={emailHandler}
              id='bulk-email'
              dataTest='bulk-email'
              large
              invalid={!!emailError}
              validationMessage={emailError}
            />
          </div>
        </div>
      </FormModal>
    )
  }
)
